import {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';

import {
  Nullable,
  PricingSheet,
  SelectSheetOption,
} from '../../../../shared/types';

import CircularProgress from '@mui/material/CircularProgress';

import { createTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Select, { OnChangeValue, ActionMeta } from 'react-select';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getUniquesByKey } from '../../utils/getUniques';
import { TextField, Typography } from '@mui/material';

import DefaultTooltip from '../Tooltips/DefaultTooltip';
import {
  getPricingSheetItemsById,
  getPricingSheetItemsMira,
} from '../../services/pricingSheetItems';
import { getPricingSheetMira } from '../../services/pricingSheets';
import ExternalSheetOption from './ExternalSheetOption';
import {
  ExternalPricingSheetItem,
  PricingSheetItemResponse,
} from '../../../../shared/types/pricingSheetItems';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EXTERNAL_SHEET_COMPARISON,
  EXTERNAL_SHEET_CONTRACT,
  EXTERNAL_SHEET_ITEMS_COMPARISON,
  EXTERNAL_SHEET_ITEMS_CONTRACT,
  SHEET_ITEMS_COMPARISON,
  SHEET_ITEMS_CONTRACT,
} from '../../../../shared/constants';
import { OfferCompareState } from '../../../../shared/types/offers';

export type ExternalPricingSheet = {
  sheetName: string;
  sheetCode: string;
  LRTBasePriceGroupId: string;
  pricingPriority: number;
  dataAreaId: string;
};

interface QueryKeyMap {
  itemsExternal: string;
  items: string;
  sheetExternal: string;
}
interface SearchExternalSheetProps {
  tooltipTitle: string;
  selectPlaceholder: string;
  label?: string;
  handleClear?: () => void;
  handleExternalItemsResponse: (
    externalPricingSheetItems: ExternalPricingSheetItem[],
  ) => void;
  handleClearComparison?: () => void;
  pricingSheetId?: string;
  comparisonPricingSheets?: PricingSheet[];
  externalPricingSheetId: Nullable<number>;
  setExternalPricingSheetId: (value?: number) => void;
  handleExternalPricingSheetResponse?: (
    externalPricingSheet: ExternalPricingSheet,
  ) => void;
  handlePricingSheetItemsResponse?: (
    pricingSheetItems: PricingSheetItemResponse[],
  ) => void;
  onSelectionChange?: (newValue: SelectSheetOption) => void;
  miraSheetIdDefault?: string;
  externalSheetNameDefault?: string;
  selectedOptionDefault?: SelectSheetOption;
  compareType?: keyof OfferCompareState;
  onLoadingChange?: (isLoading: boolean) => void;
  setMiraSheetIdExternal?: (miraId?: number) => void;
  setExternalSheetSelectedOption?: (
    newValue?: SelectSheetOption,
  ) => void;
  clearOnUnmount?: boolean;
}

const queryKeyMap: Record<string, QueryKeyMap> = {
  contractSheet: {
    itemsExternal: EXTERNAL_SHEET_ITEMS_COMPARISON,
    sheetExternal: EXTERNAL_SHEET_COMPARISON,
    items: SHEET_ITEMS_COMPARISON,
  },
  comparisonSheet: {
    itemsExternal: EXTERNAL_SHEET_ITEMS_CONTRACT,
    sheetExternal: EXTERNAL_SHEET_CONTRACT,
    items: SHEET_ITEMS_CONTRACT,
  },
};
const isOptionMiraSheet = (option?: SelectSheetOption) => {
  if (option) {
    return option.value === 'isMiraSheet';
  }
  return false;
};

const SearchExternalSheet = forwardRef<any, SearchExternalSheetProps>(
  (
    {
      tooltipTitle,
      handleExternalItemsResponse,
      handleClear,
      handleClearComparison,
      pricingSheetId,
      comparisonPricingSheets,
      externalPricingSheetId,
      setExternalPricingSheetId,
      selectPlaceholder,
      handleExternalPricingSheetResponse,
      onSelectionChange,
      handlePricingSheetItemsResponse,
      miraSheetIdDefault,
      externalSheetNameDefault,
      selectedOptionDefault,
      label,
      compareType = 'contractSheet',
      onLoadingChange,
      setMiraSheetIdExternal,
      setExternalSheetSelectedOption,
      clearOnUnmount = false,
    },
    ref,
  ) => {
    const theme = createTheme();
    const queryClient = useQueryClient();

    const selectedKeys = queryKeyMap[compareType];
    const queryKeyItemsExternal = selectedKeys.itemsExternal;
    const queryKeySheetExternal = selectedKeys.sheetExternal;
    const queryKeyItems = selectedKeys.items;

    const selectRef: any = useRef();
    const inputRef = useRef<HTMLInputElement>(null);

    const [selectedOption, setSelectedOption] = useState<
      SelectSheetOption | undefined
    >(
      (externalPricingSheetId || miraSheetIdDefault) &&
        selectedOptionDefault
        ? selectedOptionDefault
        : undefined,
    );

    const [isMiraSheet, setIsMiraSheet] = useState(
      miraSheetIdDefault && isOptionMiraSheet(selectedOptionDefault)
        ? true
        : false,
    );
    const [miraSheetId, setMiraSheetId] = useState(
      miraSheetIdDefault ?? '',
    );

    const [externalPricingSheetName, setExternalPricingSheetName] =
      useState<string | undefined>(
        isOptionMiraSheet(selectedOptionDefault) &&
          externalSheetNameDefault
          ? externalSheetNameDefault
          : undefined,
      );

    const externalItemsQuery = async () => {
      return await getPricingSheetItemsMira(
        miraSheetId ?? miraSheetIdDefault,
      );
    };

    const exeternalSheetQuery = async () => {
      return await getPricingSheetMira(
        miraSheetId ?? miraSheetIdDefault,
      );
    };

    const pricingSheetItemsQuery = async () => {
      if (selectedOption && selectedOption.id) {
        return await getPricingSheetItemsById(
          String(selectedOption.id),
        );
      }
    };

    // Remove cache for undefined id
    if (!(Boolean(miraSheetIdDefault) || Boolean(miraSheetId))) {
      queryClient.removeQueries({
        queryKey: [queryKeyItemsExternal],
      });
    }

    // Fetch items from MIRA
    const {
      data: externalSheetItems,
      isFetching: isLoadingExternalSheetItems,
      isError: isErrorExternalItems,
      isSuccess: isSuccessExternalItems,
      refetch: refetchItemsMira,
    } = useQuery({
      queryKey: [queryKeyItemsExternal],
      refetchOnWindowFocus: false,
      enabled: Boolean(miraSheetIdDefault) && !externalPricingSheetId,
      queryFn: externalItemsQuery,
    });

    // Fetch pricing sheet info from MIRA
    const {
      data: externalSheet,
      isFetching: isLoadingExternalSheet,
      isError: isErrorExternalSheet,
      isSuccess: isSuccessExternalSheet,
      refetch: refetchExternalSheets,
    } = useQuery({
      queryKey: [queryKeySheetExternal],
      refetchOnWindowFocus: false,
      enabled: Boolean(miraSheetIdDefault) && !externalPricingSheetId,
      queryFn: exeternalSheetQuery,
    });

    // Fetch items from pricing tool
    const {
      data: pricingSheetItems,
      isFetching: isLoadingPricingSheet,
      isError: isErrorPricingSheet,
      isSuccess: isSuccessPricingSheet,
      refetch: refetchPricingSheetItems,
    } = useQuery({
      queryKey: selectedOption?.id
        ? [queryKeyItems, String(selectedOption.id)]
        : [queryKeyItems],
      refetchOnWindowFocus: false,
      enabled: false,
      queryFn: pricingSheetItemsQuery,
    });

    /**
     * HELPERS
     */

    const isExternalSuccessCombined =
      isSuccessExternalSheet && isSuccessExternalItems;

    const isLoadingCombined =
      isLoadingExternalSheetItems ||
      isLoadingExternalSheet ||
      isLoadingPricingSheet;

    const isErrorCombined =
      isErrorExternalItems ||
      isErrorExternalSheet ||
      isErrorPricingSheet;

    const clearAllMiraData = () => {
      setMiraSheetId('');
      setIsMiraSheet(false);
    };

    const clearQueries = () => {
      queryClient.removeQueries({
        queryKey: [queryKeyItemsExternal],
      });
      queryClient.removeQueries({
        queryKey: [queryKeySheetExternal],
      });
    };

    const clearAllExternalData = () => {
      setMiraSheetIdExternal && setMiraSheetIdExternal(undefined);
      setExternalPricingSheetName(undefined);
      handleClearComparison && handleClearComparison();
      setExternalSheetSelectedOption &&
        setExternalSheetSelectedOption(undefined);
    };

    /**
     * HANDLERS
     */

    const handleChange = (
      newValue: OnChangeValue<SelectSheetOption, false>,
      actionMeta: ActionMeta<SelectSheetOption>,
    ) => {
      // Clear old data
      clearAllMiraData();
      clearAllExternalData();
      setSelectedOption(undefined);

      // Handle clear click
      if (actionMeta.action === 'clear') {
        handleClear && handleClear();
        return;
      }

      if (!newValue) {
        return;
      }

      // Call parent handler
      onSelectionChange && onSelectionChange(newValue);

      if (newValue.value === 'isMiraSheet') {
        setIsMiraSheet(true);
      } else {
        setExternalPricingSheetId &&
          setExternalPricingSheetId(newValue?.id);
        setSelectedOption(newValue);
      }
    };

    const handleMiraSheetIdChange = (event: any) => {
      setMiraSheetId(event.target.value);
    };

    const onBlurMiraIdInput = () => {
      if (
        miraSheetId &&
        Number(miraSheetId) !== externalPricingSheetId
      ) {
        clearQueries();
        handleClearComparison && handleClearComparison();
        setMiraSheetIdExternal &&
          setMiraSheetIdExternal(Number(miraSheetId));
        refetchExternalSheets();
        refetchItemsMira();
      }
    };

    const options: SelectSheetOption[] = comparisonPricingSheets
      ? getUniquesByKey(
          comparisonPricingSheets.map(
            (pricingSheet: PricingSheet) => {
              return {
                id: pricingSheet.id,
                value: pricingSheet.name,
                label: pricingSheet.name,
                state: pricingSheet.state,
                customerName: pricingSheet.customerName,
                from: pricingSheet.from,
                to: pricingSheet.to,
              };
            },
          ),
          'value',
        )
      : [];

    const mergedOptions: SelectSheetOption[] = [
      {
        id: 666,
        value: 'isMiraSheet',
        label: 'Hae mirasta',
      },
    ].concat(
      options
        ? options.filter(
            (sheet) => sheet.id !== Number(pricingSheetId),
          )
        : [],
    );

    useImperativeHandle(ref, () => {
      return {
        resetState() {
          setSelectedOption(undefined);
          selectRef?.current?.clearValue();
        },
      };
    });

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        inputRef.current?.blur();
      }
    };

    /**
     * EFFECTS
     */

    // Call handlers on external sheet change
    useEffect(() => {
      if (externalSheet?.sheetName && isExternalSuccessCombined) {
        setExternalPricingSheetName(externalSheet?.sheetName);
        handleExternalPricingSheetResponse &&
          handleExternalPricingSheetResponse(externalSheet);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalSheet, isExternalSuccessCombined]);

    // Call handler on external items change
    useEffect(() => {
      if (externalSheetItems && isExternalSuccessCombined) {
        handleExternalItemsResponse(externalSheetItems);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalSheetItems, isExternalSuccessCombined]);

    // Call handler on pricing sheet items change
    useEffect(() => {
      if (pricingSheetItems && isSuccessPricingSheet) {
        handlePricingSheetItemsResponse &&
          handlePricingSheetItemsResponse(pricingSheetItems);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingSheetItems, isSuccessPricingSheet]);

    // Handle loading state update on parent components
    useEffect(() => {
      if (onLoadingChange && compareType === 'contractSheet') {
        onLoadingChange(isLoadingCombined);
      }
    }, [isLoadingCombined, onLoadingChange, compareType]);

    // Reset state on unmount
    useEffect(() => {
      return () => {
        if (clearOnUnmount) {
          clearQueries();
        }
      };
    }, []);

    useEffect(() => {
      if (selectedOption?.id) {
        refetchPricingSheetItems();
      }
    }, [selectedOption?.id, refetchPricingSheetItems]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          width: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {label && <Typography fontSize={14}>{label}</Typography>}
          <DefaultTooltip placement="top" title={tooltipTitle}>
            <Select
              ref={selectRef}
              value={selectedOption}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: 400,
                  zIndex: 999,
                }),
              }}
              formatOptionLabel={ExternalSheetOption}
              isClearable={true}
              options={mergedOptions}
              placeholder={!selectedOption ? selectPlaceholder : null}
              onChange={handleChange}
            />
          </DefaultTooltip>

          {/**
           * Show error on failed request
           */}
          {!isLoadingCombined && isErrorCombined ? (
            <Typography
              sx={{
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <ErrorOutlineIcon
                style={{
                  fontSize: 16,
                  color: theme.palette.error.main,
                  marginRight: 1,
                }}
              />
              Hinnastoa ei löydy tai latauksessa tapahtui virhe
            </Typography>
          ) : null}

          {/**
           * Loading external sheets
           */}
          {isLoadingCombined ? (
            <Typography sx={{ fontSize: 14 }}>
              <CircularProgress size={14} /> Ladataan hinnaston
              tietoja...
            </Typography>
          ) : null}
          {/**
           * Loading complete
           */}
          {!isLoadingCombined &&
          !isErrorCombined &&
          externalPricingSheetName ? (
            <Typography
              style={{
                fontSize: 14,
                paddingTop: 2,
                paddingLeft: 2,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <CheckCircleOutlineIcon
                style={{
                  fontSize: 16,
                  color: theme.palette.success.main,
                  marginRight: 1,
                }}
              />
              {externalPricingSheetName}
            </Typography>
          ) : null}
          {/**
           * Take the empty space when nothing is showing
           */}
          {!isLoadingCombined &&
          !externalPricingSheetName &&
          !isErrorCombined ? (
            <Typography sx={{ minHeight: '23px' }} />
          ) : null}
        </Box>

        <Box sx={{ ml: 2, mt: label ? 2 : -1 }}>
          {isMiraSheet ? (
            <TextField
              inputRef={inputRef}
              onBlur={onBlurMiraIdInput}
              onKeyDown={handleKeyDown}
              style={{
                width: 100,
              }}
              onChange={handleMiraSheetIdChange}
              value={miraSheetId}
              size="small"
              variant="standard"
              label="Hinnaston Id"
            />
          ) : (
            <div style={{ minWidth: 100 }}></div>
          )}
        </Box>
      </Box>
    );
  },
);

export default SearchExternalSheet;
